/**
 * shareImage — 공유용 카드 이미지 한 장을 **캔버스로 굽는다** (SC-020 · 2026-08-27).
 *
 * 화면(CardSharePage)과 결과물(이 파일)이 나뉜 이유:
 *   화면은 기기 비율대로 시원하게 보여야 하고, 결과물은 어디에 올리든 같은
 *   구도여야 한다. 한동안 화면에 4:5 액자를 그려 두 가지를 한 번에 맞춰
 *   봤는데, 폰에서는 가운데로 몰려 답답했다. 그래서 액자는 **여기로만** 옮겼다.
 *   화면은 예전 구도 그대로, 나가는 그림만 4:5 다.
 *
 * ── 결과물 규격 ──────────────────────────────────────────────────
 *   1080 × 1350 (4:5) PNG — 인스타 피드·카톡 미리보기가 안 잘라내는 세로 비율.
 *
 * ── 구도 (기준 폭 343px 화면을 그대로 확대한다) ──────────────────
 *   위    카드명 · 티커/에디션/번호 · 대표시세   (왼쪽 정렬)
 *   가운데 카드 그림 — 가리는 것 없이. 번개가 오른쪽 위 모서리를 문다
 *   아래  PEEKA 워드마크
 *
 * ⚠ 색은 토큰에서 **읽어 온다**(resolveColor). 여기서 hex 를 굳혀 두면 테마를
 *   바꿨을 때 화면과 결과물의 색이 갈린다.
 * ⚠ 셰이더 배경(ShareBackdrop)은 굽지 않는다. three.js 를 오프스크린으로
 *   한 번 더 띄워야 하는데, 화면에서도 아주 옅은 겹이라 결과물에서 빠져도
 *   그림이 달라 보이지 않는다.
 */
import { CARD_GRADIENTS } from "../../data/dummyCards";
import boltSrc from "../../assets/brand/peeka-symbol-navy-orange.png";
import wordmarkSrc from "../../assets/brand/peeka-wordmark-white.png";

/** 결과물 크기 — 4:5 */
export const SHARE_IMAGE_WIDTH = 1080;
export const SHARE_IMAGE_HEIGHT = 1350;

/**
 * 구도를 잡을 때 기준으로 삼는 화면 폭. 375 기기의 액자 폭(343)이다.
 * 글자 크기·여백을 이 폭에서 재고, 결과물 폭(1080)으로 통째로 확대한다 —
 * 그래서 어느 기기에서 눌러도 같은 그림이 나온다.
 */
const REFERENCE_WIDTH = 343;

export interface ShareImageInput {
  /** 카드명 — 가장 큰 줄 */
  cardName: string;
  /** 티커 · 에디션 · 번호 한 줄 */
  meta: string;
  /** 대표시세 (또는 '시세 없음') */
  price: string;
  /** 카드 아트워크. 없으면 colorKey 그라디언트로 떨어진다 */
  imgUrl?: string;
  /** 그라디언트 키 — 배경 번짐과 아트워크 대체에 함께 쓴다 */
  /** 더미 전용 색 키. 없으면 중립 그라디언트로 떨어진다 */
  colorKey?: string;
}

/**
 * 기준 폭(343) 에서 잰 구도. 전부 px 이고, 그릴 때 scale 을 곱한다.
 * 화면 CSS 와 짝이 맞아야 하는 값들이라 어디서 왔는지 함께 적어 둔다.
 */
export interface ShareLayout {
  scale: number;
  /** 카드명이 차지한 줄 수 (1~2) */
  nameLines: number;
  pad: number;
  innerWidth: number;
  /** 글·카드·워드마크 한 덩어리의 시작 y (액자 안에서 세로 가운데) */
  contentTop: number;
  nameSize: number;
  nameLine: number;
  metaSize: number;
  metaLine: number;
  priceSize: number;
  priceLine: number;
  /** 카드명·메타·시세 사이 (VStack gap={1} = 4px) */
  textGap: number;
  /** 글 ↔ 카드 ↔ 워드마크 사이 (VStack gap={4} = 24px) */
  blockGap: number;
  cardWidth: number;
  cardHeight: number;
  cardLeft: number;
  cardRadius: number;
  boltWidth: number;
  boltHeight: number;
  wordmarkHeight: number;
  wordmarkWidth: number;
}

/**
 * 구도를 센다. 순수 함수라 테스트가 잡아 준다 — 화면 CSS 와 어긋나면
 * 여기 숫자가 먼저 틀어진다.
 */
export function computeShareLayout(nameLines = 1): ShareLayout {
  const lines = Math.min(2, Math.max(1, Math.round(nameLines)));
  const scale = SHARE_IMAGE_WIDTH / REFERENCE_WIDTH;
  /** .content 의 padding: 6% */
  const pad = REFERENCE_WIDTH * 0.06;
  const innerWidth = REFERENCE_WIDTH - pad * 2;

  /* 글 세 줄 — 화면의 Heading(20/28) · supporting(12/18.46) · 강조(16/24) */
  const nameSize = 20;
  const nameLine = 28;
  const metaSize = 12;
  const metaLine = 18.46;
  const priceSize = 16;
  const priceLine = 24;
  const textGap = 4;
  const blockGap = 24;

  /* 카드 — 안쪽 폭의 52%. 실물 비율 63:88.
     ⚠ 카드명이 두 줄이면 47% 로 한 단 줄인다. 52% 로 두면 덩어리가 액자 높이를
       꽉 채워(387.5 / 387.6) 위아래 숨 쉴 자리가 사라진다 — 서체가 조금만
       달라져도 워드마크가 밖으로 밀린다. */
  const cardWidth = innerWidth * (lines === 2 ? 0.47 : 0.52);
  const cardHeight = (cardWidth * 88) / 63;
  const cardLeft = pad + (innerWidth - cardWidth) / 2;
  /** 앞면 모서리 11px (CardFlip 과 같은 값) */
  const cardRadius = 11;

  /** 번개 — clamp(32px, 16%, 44px). 기준 폭에서는 16% 가 32 보다 작아 32 로 걸린다 */
  const boltWidth = Math.min(44, Math.max(32, cardWidth * 0.16));
  const boltHeight = (boltWidth * 260) / 152;

  const wordmarkHeight = 14;
  const wordmarkWidth = (wordmarkHeight * 1690) / 368;

  const textBlock =
    nameLine * lines + textGap + metaLine + textGap + priceLine;
  const total = textBlock + blockGap + cardHeight + blockGap + wordmarkHeight;
  const frameHeight = (REFERENCE_WIDTH * SHARE_IMAGE_HEIGHT) / SHARE_IMAGE_WIDTH;
  const contentTop = pad + Math.max(0, (frameHeight - pad * 2 - total) / 2);

  return {
    scale,
    nameLines: lines,
    pad,
    innerWidth,
    contentTop,
    nameSize,
    nameLine,
    metaSize,
    metaLine,
    priceSize,
    priceLine,
    textGap,
    blockGap,
    cardWidth,
    cardHeight,
    cardLeft,
    cardRadius,
    boltWidth,
    boltHeight,
    wordmarkHeight,
    wordmarkWidth,
  };
}

/**
 * 카드명을 폭에 맞춰 접는다 — 최대 두 줄, 넘치면 마지막 줄에 말줄임.
 *
 * ⚠ 캔버스에는 줄바꿈이 없다. 이걸 안 하면 긴 카드명(포켓몬 카드는 흔하다)이
 *   오른쪽 밖으로 그냥 흘러 나가 잘린 채로 공유된다.
 * ⚠ 한국어·일본어는 띄어쓰기 없이 길어지는 이름이 많아 **글자 단위**로도
 *   끊는다 (화면의 wordBreak="break-all" 과 같은 규칙).
 */
export function wrapCardName(
  measure: (text: string) => number,
  text: string,
  maxWidth: number,
  maxLines = 2,
): string[] {
  if (measure(text) <= maxWidth) return [text];

  const lines: string[] = [];
  let current = "";
  for (const ch of text) {
    const next = current + ch;
    if (current && measure(next) > maxWidth) {
      lines.push(current);
      current = ch === " " ? "" : ch;
      if (lines.length === maxLines - 1) break;
    } else {
      current = next;
    }
  }

  /* 남은 글자를 마지막 줄에 담되, 넘치면 말줄임으로 끝낸다 */
  const consumed = lines.join("").length;
  let rest = text.slice(consumed).replace(/^ /, "");
  if (measure(rest) > maxWidth) {
    while (rest.length > 1 && measure(`${rest}…`) > maxWidth) {
      rest = rest.slice(0, -1);
    }
    rest = `${rest}…`;
  }
  lines.push(rest);
  return lines;
}

/**
 * 토큰 값을 **화면이 실제로 쓰는 색**으로 되돌린다.
 * `--bg-page` 는 `light-dark(#CCCFFA, #0e0f1a)` 같은 값이라 캔버스가 못 읽는다.
 * 빈 요소에 얹어 브라우저가 고른 결과(rgb(...))를 받아 온다.
 */
function resolveColor(token: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const probe = document.createElement("span");
  probe.style.color = `var(${token})`;
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  document.body.appendChild(probe);
  const value = getComputedStyle(probe).color;
  probe.remove();
  return value || fallback;
}

/** 화면과 같은 서체 스택. 한글은 여기서 IBM Plex Sans KR 로 떨어진다 */
function bodyFontStack(): string {
  if (typeof document === "undefined") return "sans-serif";
  return getComputedStyle(document.body).fontFamily || "sans-serif";
}

/** `linear-gradient(160deg, #a, #b)` 에서 색 두 개만 꺼낸다 */
function gradientStops(colorKey?: string): [string, string] {
  const raw = (colorKey ? CARD_GRADIENTS[colorKey] : undefined) ?? "";
  const found = raw.match(/#[0-9a-f]{3,8}/gi);
  if (found && found.length >= 2) return [found[0], found[1]];
  if (found && found.length === 1) return [found[0], found[0]];
  return ["#4a4f7a", "#22263f"];
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    /* 외부 CDN 이 붙었을 때를 위해 — 못 받아 오면 캔버스가 오염돼 toBlob 이
       통째로 막힌다. 그때는 그림 없이(그라디언트로) 굽는 편이 낫다. */
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundedPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** 카드 색 번짐 — 화면 `.bgTint`(blur 80 · opacity .3 · 타원 마스크)를 옮긴 것 */
function drawTint(
  ctx: CanvasRenderingContext2D,
  colorKey: string | undefined,
  w: number,
  h: number,
  scale: number,
): void {
  const layer = document.createElement("canvas");
  layer.width = w;
  layer.height = h;
  const lc = layer.getContext("2d");
  if (!lc) return;

  const [from, to] = gradientStops(colorKey);
  /* 160deg — 위에서 아래로, 약간 왼쪽으로 기운다 */
  const rad = ((160 - 90) * Math.PI) / 180;
  const dx = (Math.cos(rad) * w) / 2;
  const dy = (Math.sin(rad) * h) / 2;
  const grad = lc.createLinearGradient(w / 2 - dx, h / 2 - dy, w / 2 + dx, h / 2 + dy);
  grad.addColorStop(0, from);
  grad.addColorStop(1, to);
  lc.fillStyle = grad;
  lc.fillRect(0, 0, w, h);

  /* 타원 마스크 — radial-gradient(60% 45% at 50% 42%) */
  lc.globalCompositeOperation = "destination-in";
  const mask = lc.createRadialGradient(w / 2, h * 0.42, 0, w / 2, h * 0.42, w * 0.6);
  mask.addColorStop(0, "rgba(0,0,0,1)");
  mask.addColorStop(1, "rgba(0,0,0,0)");
  lc.fillStyle = mask;
  lc.fillRect(0, 0, w, h);

  ctx.save();
  ctx.globalAlpha = 0.3;
  /* filter 를 못 쓰는 엔진에서도 그림은 나와야 한다 — 흐림만 빠진다 */
  if ("filter" in ctx) ctx.filter = `blur(${80 * scale}px) saturate(1.2)`;
  ctx.drawImage(layer, 0, 0);
  ctx.restore();
}

/** 픽셀 도트 — 화면 `.bgDots`(3px 점 · 8px 격자 · opacity .08) */
function drawDots(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  scale: number,
): void {
  const step = 8 * scale;
  const dot = 1 * scale;
  ctx.save();
  ctx.globalAlpha = 0.08;
  ctx.fillStyle = resolveColor("--border-default", "rgba(237,239,252,0.1)");
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      ctx.beginPath();
      ctx.arc(x, y, dot, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

/**
 * 공유용 이미지를 굽는다. 실패하면 null — 부르는 쪽은 글·링크만으로 넘어간다.
 * (캔버스를 못 쓰는 환경, 아트워크 CORS 실패로 캔버스가 오염된 경우 등)
 */
export async function renderShareImage(
  input: ShareImageInput,
): Promise<Blob | null> {
  if (typeof document === "undefined") return null;

  const canvas = document.createElement("canvas");
  canvas.width = SHARE_IMAGE_WIDTH;
  canvas.height = SHARE_IMAGE_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const W = SHARE_IMAGE_WIDTH;
  const H = SHARE_IMAGE_HEIGHT;
  const scale = W / REFERENCE_WIDTH;

  /* 글자가 서체 없이 구워지면 결과물만 다른 얼굴이 된다 */
  try {
    await document.fonts?.ready;
  } catch {
    /* 서체 상태를 못 물어보는 환경 — 기본 서체로 굽는다 */
  }

  /* 카드명이 몇 줄인지 먼저 재야 세로 가운데가 맞는다 — 두 줄이면 덩어리가
     그만큼 길어진다. 재는 데 쓰는 서체는 실제로 그릴 서체와 같아야 한다. */
  const fontStack = bodyFontStack();
  const probe = computeShareLayout();
  ctx.font = `600 ${probe.nameSize * scale}px ${fontStack}`;
  const nameLines = wrapCardName(
    (text) => ctx.measureText(text).width,
    input.cardName,
    probe.innerWidth * scale,
  );

  const L = computeShareLayout(nameLines.length);
  const s = L.scale;

  const [art, bolt, wordmark] = await Promise.all([
    input.imgUrl ? loadImage(input.imgUrl) : Promise.resolve(null),
    loadImage(boltSrc),
    loadImage(wordmarkSrc),
  ]);

  /* ── 배경 세 겹 ─────────────────────────────────────────────── */
  ctx.fillStyle = resolveColor("--bg-page", "#0e0f1a");
  ctx.fillRect(0, 0, W, H);
  drawTint(ctx, input.colorKey, W, H, s);
  drawDots(ctx, W, H, s);

  /* ── 글 — 왼쪽 정렬 ─────────────────────────────────────────── */
  const font = fontStack;
  const textLeft = L.pad * s;
  let y = L.contentTop * s;

  ctx.textAlign = "left";
  ctx.textBaseline = "middle";

  ctx.font = `600 ${L.nameSize * s}px ${font}`;
  ctx.fillStyle = resolveColor("--text-primary", "#EDEFFC");
  for (const line of nameLines) {
    ctx.fillText(line, textLeft, y + (L.nameLine * s) / 2);
    y += L.nameLine * s;
  }
  y += L.textGap * s;

  ctx.font = `400 ${L.metaSize * s}px ${font}`;
  ctx.fillStyle = resolveColor("--text-secondary", "#A6ACD6");
  ctx.fillText(input.meta, textLeft, y + (L.metaLine * s) / 2);
  y += (L.metaLine + L.textGap) * s;

  ctx.font = `700 ${L.priceSize * s}px ${font}`;
  ctx.fillStyle = resolveColor("--text-primary", "#EDEFFC");
  ctx.fillText(input.price, textLeft, y + (L.priceLine * s) / 2);
  y += (L.priceLine + L.blockGap) * s;

  /* ── 카드 ───────────────────────────────────────────────────── */
  const cardX = L.cardLeft * s;
  const cardY = y;
  const cardW = L.cardWidth * s;
  const cardH = L.cardHeight * s;

  ctx.save();
  roundedPath(ctx, cardX, cardY, cardW, cardH, L.cardRadius * s);
  ctx.clip();
  if (art) {
    /* cover — 비율이 달라도 카드 면이 비지 않게 채운다 */
    const ratio = Math.max(cardW / art.width, cardH / art.height);
    const dw = art.width * ratio;
    const dh = art.height * ratio;
    ctx.drawImage(art, cardX + (cardW - dw) / 2, cardY + (cardH - dh) / 2, dw, dh);
  } else {
    const [from, to] = gradientStops(input.colorKey);
    const grad = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY + cardH);
    grad.addColorStop(0, from);
    grad.addColorStop(1, to);
    ctx.fillStyle = grad;
    ctx.fillRect(cardX, cardY, cardW, cardH);
  }
  ctx.restore();

  /* 번개 — 카드 오른쪽 위 모서리를 문다 (화면 `.bolt` 와 같은 식) */
  if (bolt) {
    const bw = L.boltWidth * s;
    const bh = L.boltHeight * s;
    ctx.drawImage(bolt, cardX + cardW - bw + bw * 0.74, cardY - bh * 0.85, bw, bh);
  }

  y = cardY + cardH + L.blockGap * s;

  /* ── 워드마크 — 가운데 ──────────────────────────────────────── */
  if (wordmark) {
    const ww = L.wordmarkWidth * s;
    const wh = L.wordmarkHeight * s;
    ctx.drawImage(wordmark, (W - ww) / 2, y, ww, wh);
  }

  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), "image/png");
    } catch {
      /* 아트워크가 다른 출처라 캔버스가 오염된 경우 */
      resolve(null);
    }
  });
}
